import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'

const SERVICES = [
  {
    name: 'resend',
    label: 'Resend',
    description: 'Used for sending email notifications',
    docsUrl: 'https://resend.com/api-keys',
  },
  {
    name: 'mapbox',
    label: 'Mapbox',
    description: 'Used for address autocomplete and maps',
    docsUrl: 'https://account.mapbox.com/access-tokens/',
  },
  {
    name: 'stripe',
    label: 'Stripe',
    description: 'Payment processing',
    docsUrl: 'https://dashboard.stripe.com/apikeys',
  },
]

export default function ApiKeySettings() {
  const queryClient = useQueryClient()
  const [editingService, setEditingService] = useState(null)
  const [keyInput, setKeyInput] = useState('')
  const [showKey, setShowKey] = useState({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showAddNew, setShowAddNew] = useState(false)
  const [newService, setNewService] = useState({ name: '', description: '' })

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['system-api-keys'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_keys')
        .select('*')
        .eq('scope', 'system')
        .order('service_name')

      if (error) throw error
      return data || []
    },
  })

  const saveMutation = useMutation({
    mutationFn: async ({ serviceName, apiKey }) => {
      const { data: encryptedKey, error: encryptError } = await supabase
        .rpc('encrypt_api_key', { api_key: apiKey })

      if (encryptError) throw encryptError

      const existing = apiKeys?.find(k => k.service_name === serviceName)

      if (existing) {
        const { error } = await supabase
          .from('integration_keys')
          .update({
            encrypted_key: encryptedKey,
            is_active: true,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('integration_keys')
          .insert({
            service_name: serviceName,
            encrypted_key: encryptedKey,
            scope: 'system',
            is_active: true,
          })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['system-api-keys'])
      setEditingService(null)
      setKeyInput('')
      setSuccess('API key saved successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (serviceName) => {
      const existing = apiKeys?.find(k => k.service_name === serviceName)
      if (!existing) return

      const { error } = await supabase
        .from('integration_keys')
        .delete()
        .eq('id', existing.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['system-api-keys'])
      setSuccess('API key removed')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const handleSave = (serviceName) => {
    if (!keyInput.trim()) {
      setError('Please enter an API key')
      setTimeout(() => setError(''), 3000)
      return
    }
    saveMutation.mutate({ serviceName, apiKey: keyInput.trim() })
  }

  const getKeyStatus = (serviceName) => {
    const key = apiKeys?.find(k => k.service_name === serviceName)
    return key ? { configured: true, active: key.is_active } : { configured: false }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="spinner"></div>
      </div>
    )
  }

  const handleAddNew = () => {
    if (!newService.name.trim() || !keyInput.trim()) {
      setError('Please enter service name and API key')
      setTimeout(() => setError(''), 3000)
      return
    }

    saveMutation.mutate({
      serviceName: newService.name.toLowerCase().replace(/\s+/g, '_'),
      apiKey: keyInput,
    })
    setShowAddNew(false)
    setNewService({ name: '', description: '' })
  }

  const allServices = [...SERVICES.map(s => s.name), ...apiKeys.map(k => k.service_name)]
  const uniqueServices = [...new Set(allServices)]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">System Integrations</h3>
          <p className="text-sm text-secondary-600">Configure API keys for platform services</p>
        </div>
        <button
          onClick={() => setShowAddNew(!showAddNew)}
          className="btn-primary text-sm"
        >
          {showAddNew ? 'Cancel' : 'Add New'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {showAddNew && (
        <div className="border-2 border-primary-200 rounded-lg p-5 bg-primary-50">
          <h4 className="font-semibold text-secondary-900 mb-3">Add New Integration</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Service Name</label>
              <input
                type="text"
                value={newService.name}
                onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., sendgrid, twilio"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">API Key</label>
              <input
                type={showKey.new ? 'text' : 'password'}
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Enter API key"
                className="input"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddNew}
                disabled={saveMutation.isPending}
                className="btn-primary text-sm"
              >
                {saveMutation.isPending ? 'Adding...' : 'Add'}
              </button>
              <button
                onClick={() => {
                  setShowAddNew(false)
                  setNewService({ name: '', description: '' })
                  setKeyInput('')
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {uniqueServices.map((serviceName) => {
          const service = SERVICES.find(s => s.name === serviceName)
          const status = getKeyStatus(serviceName)
          const isEditing = editingService === serviceName
          const label = service?.label || serviceName.charAt(0).toUpperCase() + serviceName.slice(1)
          const description = service?.description || 'Custom integration'
          const docsUrl = service?.docsUrl

          return (
            <div
              key={serviceName}
              className="border border-secondary-200 rounded-lg p-5 bg-white"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-secondary-900">{label}</h3>
                    {status.configured ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        status.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {status.active ? 'Active' : 'Inactive'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-600">
                        Not configured
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-secondary-600 mb-3">{description}</p>

                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <input
                          type={showKey[serviceName] ? 'text' : 'password'}
                          value={keyInput}
                          onChange={(e) => setKeyInput(e.target.value)}
                          placeholder="Enter API key"
                          className="input w-full max-w-md"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey(prev => ({ ...prev, [serviceName]: !prev[serviceName] }))}
                          className="ml-2 text-sm text-primary-600 hover:text-primary-700"
                        >
                          {showKey[serviceName] ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(serviceName)}
                          disabled={saveMutation.isPending}
                          className="btn-primary text-sm"
                        >
                          {saveMutation.isPending ? 'Saving...' : 'Save Key'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingService(null)
                            setKeyInput('')
                          }}
                          className="btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingService(serviceName)
                          setKeyInput('')
                        }}
                        className="btn-primary text-sm"
                      >
                        {status.configured ? 'Update Key' : 'Add Key'}
                      </button>
                      {status.configured && (
                        <button
                          onClick={() => {
                            if (confirm(`Are you sure you want to remove the ${label} API key?`)) {
                              deleteMutation.mutate(serviceName)
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="btn-secondary text-sm text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                      {docsUrl && (
                        <a
                          href={docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary text-sm"
                        >
                          Get API Key
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
        <h4 className="font-medium text-secondary-900 mb-2">About API Keys</h4>
        <ul className="text-sm text-secondary-600 space-y-1">
          <li>API keys are encrypted before storage for security.</li>
          <li>System-wide keys are used by all users of the platform.</li>
          <li>Changes take effect immediately after saving.</li>
        </ul>
      </div>
    </div>
  )
}
